import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '~/hooks';
import { Account, Consent, Payroll, Transaction } from '~/types';
import { getAccounts } from '~/api/accounts';
import { useMutation } from 'react-query';
import { makePayrollTransfer } from '~/api/payrolls';
import { Alert, Button, Space, Typography } from 'antd';
import AccountAutocomplete from '~/pages/accounts/AccountAutocomplete';
import { getAmountText } from '~/components/fields/AmountField';
import styled from 'styled-components';
import theme from '~/theme';
import TransactionDetails from '~/pages/payrolls/components/TransactionDetails';
import { ApiError } from '~/types/api/ApiError';

interface AccountToDebitProps {
  data: Consent | Transaction | undefined;
  error?: ApiError;
  payroll?: Partial<Payroll>;
  goNext: (t: Transaction) => void;
}

const AccountToDebit: FC<AccountToDebitProps> = (props) => {
  const { data, payroll, error, goNext } = props;
  const [t] = useTranslation('payrolls');
  const [te] = useTranslation('errors');
  const [{ data: accounts, loading }] = useApi<Account[]>(getAccounts);

  const transfer = useMutation(makePayrollTransfer);

  return (
    <>
      <Typography.Title level={4}>{t('Account to debit')}</Typography.Title>
      {(payroll || data) && (
        <AccountAutocomplete
          disabled={true}
          accountId={data?.sender?.accountId || payroll?.senderAccount?.id}
          accounts={accounts}
          loading={loading}
        />
      )}
      <Space direction="vertical" className="full-width" size={16} style={{ marginTop: 16 }}>
        {error && (
          <Alert
            showIcon
            type="error"
            message={te([error.response?.data?.code ?? '', 'OTHER_ERROR'])}
          />
        )}
        {data?.totalAmount && !loading && (
          <>
            <Container>
              <TransactionDetails payroll={payroll} data={data} />
            </Container>
            <Button
              type="primary"
              className="SendButton"
              onClick={() => {
                transfer.mutateAsync(data?.id).then((result) => {
                  goNext(result.data?.transaction);
                });
              }}
              loading={transfer.isLoading}
            >
              {t('transfer', {
                amount: getAmountText(data?.totalAmount, true),
              })}
            </Button>
          </>
        )}
      </Space>
    </>
  );
};

const Container = styled.div`
  background: #ffffff;
  padding: 12px 16px;
  border: 1px solid #e6e6e6;
  border-bottom-left-radius: ${theme.borderRadiusBase}px;
  border-bottom-right-radius: ${theme.borderRadiusBase}px;
`;

export default AccountToDebit;
